import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Eraser,
  RotateCcw,
  Check,
  ZoomIn,
  ZoomOut,
  Crop,
  Move,
} from "lucide-react";
import { Label } from "@/components/ui/label";

interface ImageAnnotationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  imageTitle: string;
  onSave: (annotatedImageUrl: string) => void;
}

export function ImageAnnotationModal({
  open,
  onOpenChange,
  imageUrl,
  imageTitle,
  onSave,
}: ImageAnnotationModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#FF0000");
  const [brushSize, setBrushSize] = useState(3);
  const [tool, setTool] = useState<"pen" | "eraser" | "crop" | "pan">("pen");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [cropRect, setCropRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState({ x: 0, y: 0 });

  const colors = [
    { name: "Red", value: "#FF0000" },
    { name: "Blue", value: "#0000FF" },
    { name: "Green", value: "#00FF00" },
    { name: "Yellow", value: "#FFFF00" },
    { name: "Orange", value: "#FFA500" },
    { name: "Purple", value: "#800080" },
    { name: "Black", value: "#000000" },
    { name: "White", value: "#FFFFFF" },
  ];

  // Load image onto canvas
  useEffect(() => {
    if (open && imageUrl) {
      setImageLoaded(false);
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setCropRect(null);

      const timeoutId = setTimeout(() => {
        const canvas = canvasRef.current;
        const drawingCanvas = drawingCanvasRef.current;
        if (!canvas || !drawingCanvas) {
          console.error("Canvas not found");
          return;
        }

        const ctx = canvas.getContext("2d");
        const drawingCtx = drawingCanvas.getContext("2d");
        if (!ctx || !drawingCtx) {
          console.error("Could not get canvas context");
          return;
        }

        const img = new Image();

        img.onload = () => {
          const maxWidth = 800;
          const maxHeight = 600;
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          // Set both canvases to same size
          canvas.width = width;
          canvas.height = height;
          drawingCanvas.width = width;
          drawingCanvas.height = height;

          setCanvasSize({ width, height });

          // Draw base image
          ctx.drawImage(img, 0, 0, width, height);

          // Clear drawing canvas
          drawingCtx.clearRect(0, 0, width, height);

          setBaseImage(img);
          setImageLoaded(true);
        };

        img.onerror = (error) => {
          console.error("Failed to load image:", error);
          setImageLoaded(false);
        };

        img.src = imageUrl;
      }, 100);

      return () => clearTimeout(timeoutId);
    } else if (!open) {
      setImageLoaded(false);
    }
  }, [open, imageUrl]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 0.5), 3));
  };

  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    // rect.left/top already reflects the CSS pan transform on screen
    // just divide by zoom to get canvas-space coordinates
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    return { x, y };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;

    if (tool === "pan") {
      setIsPanning(true);
      setPanStart({ x: clientX - pan.x, y: clientY - pan.y });
    } else if (tool === "crop") {
      setIsCropping(true);
      setCropStart(coords);
      setCropRect({ x: coords.x, y: coords.y, width: 0, height: 0 });
    } else {
      setIsDrawing(true);
      draw(e);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;

    if (isPanning && tool === "pan") {
      setPan({
        x: clientX - panStart.x,
        y: clientY - panStart.y,
      });
      return;
    }

    if (isCropping && tool === "crop") {
      const width = coords.x - cropStart.x;
      const height = coords.y - cropStart.y;
      setCropRect({
        x: width < 0 ? coords.x : cropStart.x,
        y: height < 0 ? coords.y : cropStart.y,
        width: Math.abs(width),
        height: Math.abs(height),
      });
      return;
    }

    if (isDrawing) {
      draw(e);
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setIsPanning(false);
    setIsCropping(false);

    const canvas = drawingCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (ctx) {
      ctx.beginPath();
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing && e.type !== "mousedown") return;

    const canvas = drawingCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const coords = getCanvasCoordinates(e);

    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (tool === "eraser") {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = selectedColor;
    }

    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const applyCrop = () => {
    if (!cropRect || !canvasRef.current || !drawingCanvasRef.current) return;

    const canvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const drawingCtx = drawingCanvas.getContext("2d");
    if (!ctx || !drawingCtx) return;

    // Create temporary canvases for cropped content
    const tempBaseCanvas = document.createElement("canvas");
    const tempDrawingCanvas = document.createElement("canvas");
    tempBaseCanvas.width = cropRect.width;
    tempBaseCanvas.height = cropRect.height;
    tempDrawingCanvas.width = cropRect.width;
    tempDrawingCanvas.height = cropRect.height;

    const tempBaseCtx = tempBaseCanvas.getContext("2d");
    const tempDrawingCtx = tempDrawingCanvas.getContext("2d");

    if (tempBaseCtx && tempDrawingCtx) {
      // Copy cropped portions
      tempBaseCtx.drawImage(
        canvas,
        cropRect.x,
        cropRect.y,
        cropRect.width,
        cropRect.height,
        0,
        0,
        cropRect.width,
        cropRect.height,
      );

      tempDrawingCtx.drawImage(
        drawingCanvas,
        cropRect.x,
        cropRect.y,
        cropRect.width,
        cropRect.height,
        0,
        0,
        cropRect.width,
        cropRect.height,
      );

      // Resize canvases
      canvas.width = cropRect.width;
      canvas.height = cropRect.height;
      drawingCanvas.width = cropRect.width;
      drawingCanvas.height = cropRect.height;

      setCanvasSize({ width: cropRect.width, height: cropRect.height });

      // Draw cropped content
      ctx.drawImage(tempBaseCanvas, 0, 0);
      drawingCtx.drawImage(tempDrawingCanvas, 0, 0);
    }

    // Reset zoom, pan, and crop
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setCropRect(null);
    setTool("pen");
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    const drawingCtx = drawingCanvas?.getContext("2d");

    if (!canvas || !drawingCanvas || !ctx || !drawingCtx || !baseImage) return;

    // Redraw base image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

    // Clear annotations
    drawingCtx.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);

    setZoom(1);
    setPan({ x: 0, y: 0 });
    setCropRect(null);
  };

  const handleSave = () => {
    const baseCanvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    if (!baseCanvas || !drawingCanvas) return;

    // Create a final canvas that combines both layers
    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = baseCanvas.width;
    finalCanvas.height = baseCanvas.height;
    const finalCtx = finalCanvas.getContext("2d");

    if (finalCtx) {
      // Draw base image
      finalCtx.drawImage(baseCanvas, 0, 0);
      // Draw annotations on top
      finalCtx.drawImage(drawingCanvas, 0, 0);
    }

    const annotatedImageUrl = finalCanvas.toDataURL("image/png");
    onSave(annotatedImageUrl);
    onOpenChange(false);
  };

  const getCursorStyle = () => {
    switch (tool) {
      case "pan":
        return isPanning ? "grabbing" : "grab";
      case "crop":
        return "crosshair";
      case "eraser":
        return "crosshair";
      default:
        return "crosshair";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Annotate Image: {imageTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border flex-wrap">
            {/* Tool Selection */}
            <div className="flex gap-2">
              <Button
                variant={tool === "pen" ? "default" : "outline"}
                size="sm"
                onClick={() => setTool("pen")}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Pen
              </Button>
              <Button
                variant={tool === "eraser" ? "default" : "outline"}
                size="sm"
                onClick={() => setTool("eraser")}
              >
                <Eraser className="w-4 h-4 mr-2" />
                Eraser
              </Button>
              <Button
                variant={tool === "crop" ? "default" : "outline"}
                size="sm"
                onClick={() => setTool("crop")}
              >
                <Crop className="w-4 h-4 mr-2" />
                Crop
              </Button>
              <Button
                variant={tool === "pan" ? "default" : "outline"}
                size="sm"
                onClick={() => setTool("pan")}
              >
                <Move className="w-4 h-4 mr-2" />
                Pan
              </Button>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 min-w-[120px]">
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs text-gray-600 w-12">
                  {Math.round(zoom * 100)}%
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Color Selection */}
            <div className="flex items-center gap-2">
              <Label className="text-xs">Color:</Label>
              <div className="flex gap-1">
                {colors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => {
                      setSelectedColor(color.value);
                      setTool("pen");
                    }}
                    className={`w-8 h-8 rounded border-2 transition-all ${
                      selectedColor === color.value
                        ? "border-gray-900 scale-110"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Brush Size */}
            <div className="flex items-center gap-2">
              <Label className="text-xs">Size:</Label>
              <input
                type="range"
                min="1"
                max="20"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-600 w-6">{brushSize}</span>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Action Buttons */}
            <div className="flex gap-2">
              {tool === "crop" && cropRect && (
                <Button variant="default" size="sm" onClick={applyCrop}>
                  <Crop className="w-4 h-4 mr-2" />
                  Apply Crop
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={clearCanvas}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>

          {/* Canvas Container */}
          <div
            ref={containerRef}
            className="flex justify-center items-center bg-gray-100 rounded-lg p-4 min-h-[400px] max-h-[600px] overflow-auto"
            onWheel={handleWheel}
          >
            <div
              className="relative"
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transformOrigin: "top left",
                transition: isPanning ? "none" : "transform 0.1s ease-out",
              }}
            >
              {/* Base Image Canvas */}
              <canvas
                ref={canvasRef}
                className={`border-2 border-gray-300 rounded shadow-lg bg-white absolute top-0 left-0 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
              />

              {/* Drawing Canvas (on top) */}
              <canvas
                ref={drawingCanvasRef}
                onMouseDown={startDrawing}
                onMouseMove={handleMouseMove}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className={`border-2 border-gray-300 rounded shadow-lg absolute top-0 left-0 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  cursor: getCursorStyle(),
                  pointerEvents: "auto",
                }}
              />

              {/* Crop Rectangle Overlay */}
              {tool === "crop" && cropRect && imageLoaded && (
                <div
                  className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-10 pointer-events-none"
                  style={{
                    left: cropRect.x,
                    top: cropRect.y,
                    width: cropRect.width,
                    height: cropRect.height,
                    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.4)",
                  }}
                />
              )}

              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-gray-500">Loading image...</div>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="text-xs text-gray-500 text-center">
            {tool === "pen" && "Click and drag to draw on the image"}
            {tool === "eraser" && "Click and drag to erase annotations"}
            {tool === "crop" &&
              "Click and drag to select crop area, then click 'Apply Crop'"}
            {tool === "pan" && "Click and drag to pan around the zoomed image"}
            {" • Use mouse wheel to zoom in/out"}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Check className="w-4 h-4 mr-2" />
            Save Annotated Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
